import { cn } from '@/lib/utils';
import { Check, X } from 'lucide-react';

type CheckpointControlSize = 'sm' | 'lg';

interface CheckpointControlProps {
  value: boolean | null;
  onChange: (value: boolean | null) => void;
  disabled?: boolean;
  /** Visual size variant. `sm` (default) for compact tree-editor rows;
   *  `lg` for detail panels (taller, icon + label, full-width). */
  size?: CheckpointControlSize;
}

/**
 * Tri-state pass/fail control for checkpoint fields.
 * Toggle logic: clicking the already-selected state resets to null.
 *
 * Visual variants:
 *  - `sm` (default): w-[100px], h-8, icon-only buttons (tree editor).
 *  - `lg`: flex-1 (no fixed width), h-12, icon + label text, border-2,
 *    rounded-lg, shadow on selected (question detail panel).
 */
export function CheckpointControl({
  value,
  onChange,
  disabled,
  size = 'sm',
}: CheckpointControlProps) {
  const handlePass = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled) return;
    onChange(value === true ? null : true);
  };

  const handleFail = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled) return;
    onChange(value === false ? null : false);
  };

  const isLg = size === 'lg';

  return (
    <div
      className={cn('flex items-center', isLg ? 'gap-2' : 'w-[100px] shrink-0')}
    >
      <button
        type="button"
        disabled={disabled}
        onClick={handlePass}
        className={cn(
          'flex flex-1 items-center justify-center transition-colors',
          isLg
            ? `h-12 gap-2 rounded-lg border-2 text-sm font-medium transition-all
              focus-visible:ring-2`
            : 'h-8 rounded-l-md border text-sm focus-visible:ring-1',
          'focus-visible:ring-ring focus-visible:outline-none',
          value === true
            ? isLg
              ? 'border-green-600 bg-green-600 text-white shadow-sm'
              : 'border-green-600 bg-green-600 text-white'
            : isLg
              ? 'border-border text-green-700 hover:border-green-300 hover:bg-green-50'
              : `border-input text-green-700 hover:bg-green-50 dark:text-green-500
                dark:hover:bg-green-950/30`,
          disabled && 'cursor-not-allowed opacity-50',
        )}
        aria-label="Pass"
      >
        <Check className={isLg ? 'h-5 w-5' : 'h-4 w-4'} strokeWidth={2.5} />
        {isLg && 'Pass'}
      </button>
      <button
        type="button"
        disabled={disabled}
        onClick={handleFail}
        className={cn(
          'flex flex-1 items-center justify-center transition-colors',
          isLg
            ? `h-12 gap-2 rounded-lg border-2 text-sm font-medium transition-all
              focus-visible:ring-2`
            : 'h-8 rounded-r-md border text-sm focus-visible:ring-1',
          'focus-visible:ring-ring focus-visible:outline-none',
          value === false
            ? isLg
              ? 'border-red-600 bg-red-600 text-white shadow-sm'
              : 'border-red-600 bg-red-600 text-white'
            : isLg
              ? 'border-border text-red-700 hover:border-red-300 hover:bg-red-50'
              : `border-input text-red-700 hover:bg-red-50 dark:text-red-500
                dark:hover:bg-red-950/30`,
          disabled && 'cursor-not-allowed opacity-50',
        )}
        aria-label="Fail"
      >
        <X className={isLg ? 'h-5 w-5' : 'h-4 w-4'} strokeWidth={2.5} />
        {isLg && 'Fail'}
      </button>
    </div>
  );
}
